import _extends from '@babel/runtime/helpers/esm/extends';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import React, { useState, useContext, useMemo, useEffect } from 'react';
import getComponentFromProps from '@elliemae/ds-utilities/getComponentFromProps';
import DSPopper from '@elliemae/ds-basic/Popper';
import { Menu } from '@elliemae/ds-basic/Menu';
import { convertPropToCssClassName } from '@elliemae/ds-classnames';
import { isFunction, runAll } from '@elliemae/ds-utilities/utils';
import DropdownMenuContext from './DropdownMenuContext.js';
import { factory, renderOptions } from './decoratedMenuItemFactory.js';
import LoadingIndicator from './DropdownMenuLoadingIndicator.js';
import './MenuItemClickableHOC.js';
import '@babel/runtime/helpers/esm/objectWithoutProperties';
import './MenuItemGroupSelectionHOC.js';
import 'styled-components';
import '@elliemae/ds-basic/CircularProgressIndicator';

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
var blockName = 'menu';

var getMax = function getMax(options) {
  var max = 0;
  options.forEach(function (o) {
    if (o && o.label && String(o.label).length > max) max = String(o.label).length;
  });
  return max;
};

var DropdownMenuRenderer = function DropdownMenuRenderer(_ref) {
  var containerProps = _ref.containerProps,
      className = _ref.className,
      _ref$options = _ref.options,
      options = _ref$options === void 0 ? [] : _ref$options,
      triggerComponent = _ref.triggerComponent,
      placement = _ref.placement,
      interactionType = _ref.interactionType,
      preventOverflow = _ref.preventOverflow,
      customMenu = _ref.customMenu,
      contentOffset = _ref.contentOffset,
      contentStyle = _ref.contentStyle,
      isOpen = _ref.isOpen,
      children = _ref.children,
      menuProps = _ref.menuProps,
      referenceNode = _ref.referenceNode,
      focusOnOpenProp = _ref.focusOnOpen,
      onClickOutsideMenu = _ref.onClickOutsideMenu,
      responsiveHeight = _ref.responsiveHeight,
      itemsRenderer = _ref.itemsRenderer,
      maxWidth = _ref.maxWidth,
      minWidth = _ref.minWidth,
      zIndex = _ref.zIndex,
      type = _ref.type,
      loading = _ref.loading,
      hideIfNotVisible = _ref.hideIfNotVisible;

  var _useState = useState(focusOnOpenProp),
      _useState2 = _slicedToArray(_useState, 2),
      focusOnOpen = _useState2[0],
      setFocusOnOpenMenu = _useState2[1];

  var _useContext = useContext(DropdownMenuContext),
      isMenuOpen = _useContext.isMenuOpen,
      openMenu = _useContext.openMenu,
      closeMenu = _useContext.closeMenu;

  var maxOption = useMemo(function () {
    var max = 0;
    options.forEach(function (o) {
      if (o && o.label && String(o.label).length > max) max = String(o.label).length;

      if (o && o.items) {
        var nestedMax = getMax(o.items);
        if (nestedMax > max) max = nestedMax;
      }
    });
    return max;
  }, [options]);
  var optionsRendererd = useMemo(function () {
    if (options[0] && options[0].type === 'separator') {
      options[0].showSeparator = false;
    }

    return isFunction(itemsRenderer) ? itemsRenderer(options, factory) : renderOptions(options);
  }, [options]);
  var dropdownBlockName = convertPropToCssClassName(blockName);

  var menuContent = function menuContent() {
    if (loading) return /*#__PURE__*/React.createElement(LoadingIndicator, null);
    if (children) return children;
    if (optionsRendererd) return optionsRendererd;
  };

  var MenuComponent = customMenu ? getComponentFromProps(customMenu, _objectSpread(_objectSpread({
    options: options,
    focusOnOpen: focusOnOpen,
    maxOption: maxOption
  }, menuProps), {}, {
    closeMenu: closeMenu,
    loading: loading
  })) : /*#__PURE__*/React.createElement(Menu, _extends({
    closeMenu: closeMenu,
    focusOnOpen: focusOnOpen,
    maxOption: type === 'phone' ? maxOption : undefined,
    maxWidth: maxWidth,
    minWidth: minWidth,
    onClickOutside: onClickOutsideMenu,
    onExitFocusGroup: closeMenu,
    responsiveHeight: responsiveHeight,
    type: type,
    visible: true
  }, menuProps), menuContent());
  useEffect(function () {
    setFocusOnOpenMenu(focusOnOpenProp);
  });
  return /*#__PURE__*/React.createElement(DSPopper, {
    hideIfNotVisible: hideIfNotVisible,
    blockName: dropdownBlockName,
    containerProps: containerProps,
    contentComponent: MenuComponent,
    contentOffset: contentOffset,
    contentStyle: contentStyle,
    interactionType: interactionType,
    isOpen: isOpen !== undefined ? isOpen : isMenuOpen,
    onOpen: function onOpen(opening) {
      return opening ? openMenu() : closeMenu();
    },
    placement: placement,
    preventOverflow: preventOverflow,
    referenceNode: referenceNode,
    showArrow: false,
    triggerComponent: getComponentFromProps(triggerComponent, {
      onClick: interactionType === 'hover' ? runAll(openMenu, triggerComponent.props.onClick) : runAll(!isMenuOpen ? openMenu : closeMenu, triggerComponent.props.onClick),
      onMouseEnter: interactionType === 'hover' ? openMenu : undefined,
      onKeyDown: function onKeyDown(e) {
        if (e.key === 'ArrowDown' || e.key === ' ') {
          e.preventDefault();
          setFocusOnOpenMenu(true);
          openMenu();
        }
      },
      className: "".concat(triggerComponent.props.className, " ").concat(className)
    }),
    zIndex: zIndex || menuProps.zIndex
  });
};

export default DropdownMenuRenderer;
//# sourceMappingURL=DropdownMenuRenderer.js.map
